import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../../i18n';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n: i18next, t } = useTranslation('common');
  const [lang, setLangState] = useState(() => i18next.language || 'en');

  // Update language state on language change
  useEffect(() => {
    const handler = (lng) => {
      setLangState(lng);
      localStorage.setItem('lang', lng);
    };
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);

  const setLang = async (lng) => {
    await i18n.changeLanguage(lng);
  };

  // Hydrate on first mount from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('lang');
    if (stored && stored !== i18n.language) {
      i18n.changeLanguage(stored);
    }
  }, []);

  const value = useMemo(() => ({
    lang,
    setLang,
    t,
    i18n,
    isRTL: lang === 'ar',
    currentLanguage: lang,
    direction: lang === 'ar' ? 'rtl' : 'ltr',
  }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};